/* tslint:disable:component-selector import-spacing no-trailing-whitespace no-inferrable-types */

import {
  Component,
  AfterViewInit,
  OnDestroy,
  EventEmitter,
  Output, NgZone
} from '@angular/core';

import { Logger            } from '@/util/logger';
import {
  errorAsText
} from '@/util/util';

import { AlertService           } from "@/services/alert.service";
import { AuthenticationService  } from "@/services/authentication.service";
import { ActionService          } from "@/services/action.service";
import { Event                  } from "@/components/krpano/events/event";
import { EventType              } from "@/components/krpano/events/event.type";
import { NewPanoEvent           } from "@/components/krpano/events/new.pano.event";
import { ErrorEvent             } from "@/components/krpano/events/error.event";
import { KeyboardEvent          } from "@/components/krpano/events/keyboard.event";
import { ClickEvent             } from "@/components/krpano/events/click.event";
import { ResizeEvent            } from "@/components/krpano/events/resize.event";
import { FramebufferResizeEvent } from "@/components/krpano/events/framebuffer.resize.event";
import { WheelEvent             } from "@/components/krpano/events/wheel.event";
import {ActivatedRoute, ParamMap, Params, Router} from "@angular/router";
import {first} from "rxjs/operators";
import {PanoramaService} from "@/services/panorama.service";
import {Panorama} from "@/models/panorama";
import {PanoramaSelection} from "@/models/panorama.selection";

@Component({
  standalone: false,
  selector: 'panorama-viewer-wrapper',
  template: `
    <div class="panorama-viewer-component-bg-wrapper">
      <div id="panorama-viewer-progress" class="panorama-viewer-progress"></div>
      <div id="panorama-viewer-component" class="panorama-viewer-component"></div>
    </div>
  `
})

/**
 *
 * @class KRPanoramaViewer
 *
 * @brief angular component that wraps the KRPano Javascript based viewer.  This is
 * the raw panoramic viewer piece.
 *
 * Home page for krpano: https://krpano.com/home/
 *
 * Events:  https://krpano.com/docu/xml/#events
 *
 * Variables: https://krpano.com/docu/actions/#globalvarsreference
 *
 * We use "flat" or Rectilinear format: https://hugin.sourceforge.io/docs/manual/Panorama_formats.html#Rectilinear
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2022
 *
 */
export class KRPanoramaViewer implements AfterViewInit, OnDestroy {

  /**
   * the root folder for Google Bucket based panorama photo storage (flat format)
   * the prefix used sets up where we are streaming in the panorama tiles from.
   *
   * Docker Container Proxy
   * ----------------------
   *
   * /backend/image/proxy/flat - the Docker cotnainer will proxy through from our
   * private google bucket. Not entirely preferred; its secure and works well,
   * except that it places a lot of pressure on the Docker container.  All those
   * panorama tile images get pre-loaded by KRPano and that can overload Apache,
   * fill memory in the container, or create a bottleneck on disk, where the
   * images being proxies are stored before streaming back to the browser.
   *
   * Google Bucket (Public)
   * ----------------------
   *
   * https://yrailviewpanos.storage.googleapis.com/flat - we're using a public
   * Google Bucket; notice we are NOT using storage.googleapis.com, because that
   * one does not send CORS headers.
   *
   * Note that its not enough to enable public access to the Bucket; because
   * we are scripted access (JavaScript), browsers require CORS protocol, so
   * you have to ensure the bucket has an open CORS specification:
   *
   *   https://cloud.google.com/storage/docs/cross-origin
   *
   * so that we don't get "access denied" on the browser side, for lack of
   * CORS headers in the Google Bucket response.  The CORS configuration:
   *
   *   https://stackoverflow.com/questions/54643222/google-cloud-storage-cors-policy-allow-all
   *   https://kevinsimper.medium.com/google-cloud-storage-cors-not-working-after-enabling-14693412e404
   *
   * we just completely open:
   *
   *   [
   *     {
   *       "maxAgeSeconds": 86400,
   *       "method": ["GET", "POST", "HEAD"],
   *       "origin": ["*"],
   *       "responseHeader": ["*"]
   *     }
   *   ]
   *
   * You can configure with 'gsutil':
   *
   *    https://cloud.google.com/storage/docs/configuring-cors#configure-cors-bucket
   *
   *  gsutil cors set cors.json gs://railviewpanos
   *
   * Google CLoud CDN
   * ----------------
   *
   *  https://cdn.railview.ironroad.ca/flat - we're using Google CDN (content deliver network),
   *  which on the backend is using a private Google bucket.  This is the preferred solution,
   *  Google does all the heavy lifting, minimal pressure on our Docker container.  Should
   *  give users the best possible image transfer times (CDN edge servers geographically
   *  located the nearest users).
   *
   *  Note that we can't use signed cookies; KRPano doesn't support sending cookies when it
   *  requests images.  We can't use fully signed URLs either, because we would have to know
   *  the evaluated URL that KRPano generates...which we don't know.  But, we can sign a
   *  commong prefix to all URLs (Google CDN supports URLPrefix signing method).  In this
   *  mode we just append our signature that was created for the base of the URLs, and
   *  ensure it has a reasonable expiry and that it was generated by logged-in user...we
   *  satisfy the later conditions by obtaining the signature from our backend. Yup,
   *  we yank it out of our butt.
   *
   */
  private storageBase: string    = 'https://cdn.railview.ironroad.ca/flat';

  /** the #ID we use for element that will carry the viewer */
  private static domId: string   = 'panorama-viewer-component';

  /** the KRPano API object */
  private krpano                 = null;

  /** only true before first load() */
  private _firstLoad: boolean    = true;

  /** the metadata for the currently loaded panorama */
  private _meta: Panorama       = null;

  /** the metadata for the currently loaded panorama */
  private _previousMeta: Panorama = null;

  /** current horizontal angle */
  private _h: number             = 0;

  /** current vertical angle */
  private _v: number             = 0;

  /** current FOV */
  private _fov: number           = 90;

  /** previous FOV */
  private _oldFOV: number        = 90;

  /** horizontal angle to take effect on next load complete */
  private _pendingH: number      = 0;

  /** vertical angle to take effect on next load complete */
  private _pendingV: number      = 0;

  /** FOV to take effect on next load complete */
  private _pendingFOV: number    = 90;

  /**
   *  we sometimes call loadViewParameters more than once, but it should only
   *  run once for each save.
   */
  private _viewRestored: boolean = false;

  /** make true to log all events (verbose mode) */
  private loggingEvents: boolean = false;

  /** true while the control key is down */
  private _ctrlDown: boolean     = false;

  /** true while the shift key is down */
  private _shiftDown: boolean    = false;

  /** true while the alt key is down */
  private _altDown: boolean      = false;

  @Output() pano_event           = new EventEmitter<Event>();

  /**
   * when not null, we are currently in the process of loading or rendering
   * we expect that the lock is obtained when jumpTo() (below), when we start
   * a jump to a new panorama.  The unlocking has two steps though; target
   * is set back to null when KRPano has finished loading (even if not all images
   * are done), but the screen attribute is only set back to true once the
   * rest of the application is done rendering screen controls etc.  (i.e.
   * when leaflet is done).
   *
   * this way we can ensure everything has "settled" before we try to do a jump
   * again.  This is important because there is a lot of asynchronous calls to
   * the backend happening in both leaflet and krpano, so depending on timing,
   * you could end up with a lot of latent javascript callbacks happening, and
   * you could end up jumping back to panos that were partially started but then
   * a jump happened again.
   *
   */
  private _locked: {target: Panorama, screen: boolean} = {target: null, screen: true};

  /** standard constructor */
  constructor(
    private alerts:  AlertService,
    private auth:    AuthenticationService,
    private actions: ActionService,
    private router:  Router,
    private route:   ActivatedRoute,
    private panos:   PanoramaService,
    private readonly zone: NgZone
  ) {

    Logger.debug('[KRPanoramaViewer] constructing...');

  }

  /** setup the KRPano viewer */
  ngAfterViewInit(): void {

    Logger.debug('[KRPanoramaViewer] after view init...');

    let kpc: KRPanoramaViewer = this;

    kpc.zone.runOutsideAngular(() => {

      window.embedpano({
        target: KRPanoramaViewer.domId,
        xml: 'assets/krpano-1.22.3/krpano.xml',
        html5: 'only',
        focus: true,
        bgcolor: 'transparent',
        consolelog: true,
        onready: (krpano) => {

          kpc.krpano = krpano.get("global");

          kpc.onReady();
        },
        onerror: (msg) => kpc.onError(msg),
        initvars: {
          'ironroad.storage': kpc.storageBase,

          'events.onenterfullscreen': () => {

            kpc.publishEvent(new Event(kpc.krpano, EventType.ENTER_FULLSCREEN));

          },

          'events.onexitfullscreen': () => {

            kpc.publishEvent(new Event(kpc.krpano, EventType.EXIT_FULLSCREEN));
          },

          'events.onxmlcomplete': () => {

            kpc.publishEvent(new Event(kpc.krpano, EventType.XML_COMPLETE));
          },

          'events.onloadcomplete': () => {

            Logger.debug('[KRPanoramaViewer] loading completed.');

            kpc.loadViewParameters();

            kpc.publishEvent(new Event(kpc.krpano, EventType.LOAD_COMPLETE));

            kpc.unlockPanoViewer();
          },

          'events.onblendcomplete': () => {

            Logger.debug('[KRPanoramaViewer] blending completed.');

            kpc.loadViewParameters();

            kpc.publishEvent(new Event(kpc.krpano, EventType.BLEND_COMPLETE));

            kpc.unlockPanoViewer();
          },

          'events.onnewpano': () => {

            kpc.publishEvent(new NewPanoEvent(kpc.krpano, kpc.krpano.image.type, kpc.krpano.image.hfov, kpc.krpano.image.vfov));
          },

          'events.onremovepano': () => {

            kpc.publishEvent(new Event(kpc.krpano, EventType.REMOVE_PANO));
          },

          'events.onnewscene': () => {
            Logger.debug('[KRPanoramaViewer] onnewscene.');
          },

          'events.onxmlerror': () => {

            if (!kpc.meta().enabled) {

              kpc.krpano.lasterror = `[Panorama DISABLED] ${kpc.krpano.lasterror}`;
            }

            kpc.alerts.error(kpc.krpano.lasterror);

            kpc.publishEvent(new ErrorEvent(kpc.krpano, 'xml', kpc.krpano.lasterror));
          },

          'events.onloaderror': () => {

            if (!kpc.meta().enabled) {

              kpc.krpano.lasterror = `[Panorama DISABLED] ${kpc.krpano.lasterror}`;
            }

            kpc.alerts.error(kpc.krpano.lasterror);

            kpc.publishEvent(new ErrorEvent(kpc.krpano, 'load', kpc.krpano.lasterror));
          },

          'events.onkeydown': () => {

            if (kpc.krpano.keycode == '16') {

              /* shift key */

              kpc._shiftDown = true;

            } else if (kpc.krpano.keycode == '17') {

              /* ctrl key */

              kpc._ctrlDown = true;

            } else if (kpc.krpano.keycode == '18') {

              /* alt key */

              kpc._altDown = true;
            }

            kpc.publishEvent(new KeyboardEvent(kpc.krpano, kpc.krpano.keycode, false, kpc._altDown, kpc._shiftDown, kpc._ctrlDown));
          },

          'events.onkeyup': () => {

            if (kpc.krpano.keycode == '16') {

              /* shift key */

              kpc._shiftDown = false;

            } else if (kpc.krpano.keycode == '17') {

              /* ctrl key */

              kpc._ctrlDown = false;

            } else if (kpc.krpano.keycode == '18') {

              /* alt key */

              kpc._altDown = false;
            }

            kpc.publishEvent(new KeyboardEvent(kpc.krpano, kpc.krpano.keycode, true, kpc._altDown, kpc._shiftDown, kpc._ctrlDown));
          },

          'events.onclick': () => {

            kpc.publishEvent(new ClickEvent(
              kpc.krpano,
              EventType.CLICK,
              true,
              true,
              true,
              kpc.krpano.mouse.clickx,
              kpc.krpano.mouse.clicky));
          },

          'events.onsingleclick': () => {

            kpc.publishEvent(new ClickEvent(
              kpc.krpano,
              EventType.SINGLE_CLICK,
              true,
              true,
              true,
              kpc.krpano.mouse.clickx,
              kpc.krpano.mouse.clicky));
          },

          'events.ondoubleclick': () => {

            kpc.publishEvent(new ClickEvent(
              kpc.krpano,
              EventType.DOUBLE_CLICK,
              true,
              false,
              true,
              kpc.krpano.mouse.clickx,
              kpc.krpano.mouse.clicky));
          },

          'events.onmousedown': () => {

            kpc.publishEvent(new ClickEvent(
              kpc.krpano,
              EventType.MOUSE_DOWN,
              true,
              true,
              false,
              kpc.krpano.mouse.clickx,
              kpc.krpano.mouse.clicky));
          },

          'events.onmouseup': () => {
            kpc.publishEvent(new ClickEvent(
              kpc.krpano,
              EventType.MOUSE_UP,
              true,
              true,
              true,
              kpc.krpano.mouse.clickx,
              kpc.krpano.mouse.clicky));
          },

          'events.onmousewheel': () => {

            kpc.publishEvent(new WheelEvent(
              kpc.krpano,
              kpc.krpano.wheeldelta,
              kpc.krpano.wheeldelta_raw,
              kpc.krpano.wheeldelta_touchscale));
          },

          'events.oncontextmenu': () => {

            kpc.publishEvent(new ClickEvent(
              kpc.krpano,
              EventType.CONTEXT_MENU,
              false,
              true,
              true,
              kpc.krpano.mouse.x,
              kpc.krpano.mouse.y));
          },

          'events.onidle': () => {

            kpc.publishEvent(new Event(kpc.krpano, EventType.IDLE));
          },

          'events.onviewchange': () => {

            kpc.publishEvent(new Event(kpc.krpano, EventType.VIEW_CHANGE));

          },

          'events.onviewchanged': () => {

            kpc._h = kpc.normalize180(kpc.krpano.view.hlookat);

            kpc._v = kpc.krpano.view.vlookat;
            kpc._fov = kpc.krpano.view.fov < 30 ? 30 : kpc.krpano.view.fov;

            kpc.publishEvent(new Event(kpc.krpano, EventType.VIEW_CHANGED));

            kpc._oldFOV = kpc._fov;
          },

          'events.onresize': () => {

            if (kpc.krpano) {

              kpc.publishEvent(new ResizeEvent(kpc.krpano, kpc.krpano.stagewidth, kpc.krpano.stageheight, kpc.krpano.stagescale));
            }

          },

          'events.onframebufferresize': () => {

            if (kpc.krpano) {

              kpc.publishEvent(new FramebufferResizeEvent(
                kpc.krpano,
                kpc.krpano.stagewidth,
                kpc.krpano.stageheight,
                kpc.krpano.stagescale,
                kpc.krpano.display.framebufferscale));
            }

          },

          'events.onautorotatestart': () => {
            Logger.debug('[KRPanoramaViewer] onautorotatestart.');

            kpc.publishEvent(new Event(kpc.krpano, EventType.AUTO_ROTATE_START));
          },

          'events.onautorotatestop': () => {
            Logger.debug('[KRPanoramaViewer] onautorotatestop.');

            kpc.publishEvent(new Event(kpc.krpano, EventType.AUTO_ROTATE_STOP));
          },

          'events.onautorotateoneround': () => {
            Logger.debug('[KRPanoramaViewer] onautorotateoneround.');

            kpc.publishEvent(new Event(kpc.krpano, EventType.AUTO_ROTATE_ONEROUND));
          },

          'events.onautorotatechange': () => {
            Logger.debug('[KRPanoramaViewer] onautorotatechange.');

            kpc.publishEvent(new Event(kpc.krpano, EventType.AUTO_ROTATE_CHANGE));
          },

          'events.oniphonefullscreen': () => {

            kpc.publishEvent(new Event(kpc.krpano, EventType.IPHONE_FULLSCREEN));
          },
        }

      });

    });

  }

  /** lock the application from trying to start a new jump to a different panorama */
  lock(pano: Panorama): {target: Panorama, screen: boolean} {

    let kpc: KRPanoramaViewer = this;

    if (!pano) {

      /* if they mean to unlock, they have to call unlock! */

      Logger.warning(`[KRPanoramaViewer] lock - called with null panorama!`);

      return kpc._locked;
    }

    Logger.warning(`[KRPanoramaViewer] lock - LOCK pending panorama: ${pano.id}`);

    kpc._locked.target = pano;
    kpc._locked.screen = false;

    $(`.panorama-viewer-progress`).css('display', 'block');

    return kpc._locked;
  }

  /** partial unlock, unlock KRPano, its done rendering the panorama */
  unlockPanoViewer(): {target: Panorama, screen: boolean} {

    let kpc: KRPanoramaViewer = this;

    if (kpc._locked.target === null) {

      Logger.warning(`[KRPanoramaViewer] - unlockPanoViewer already unlocked.`);

      return ;
    }

    Logger.warning(`[KRPanoramaViewer] - UNLOCK unlockPanoViewer from pano: ${kpc._locked.target.id}`);

    kpc._locked.target = null;

    if (!kpc.isLocked()) {

      $(`.panorama-viewer-progress`).css('display', 'none');
    }

    return kpc._locked;
  }

  /** partial unlock, unlock application, it's done rendering anything else on screen */
  unlockApplication(): {target: Panorama, screen: boolean} {

    let kpc: KRPanoramaViewer = this;

    if (kpc._locked.screen === true) {

      Logger.warning(`[KRPanoramaViewer] - unlockApplication already unlocked.`);

      return ;
    }

    Logger.warning(`[KRPanoramaViewer] - UNLOCK unlockApplication screen unlocked.`);

    kpc._locked.screen = true;

    if (!kpc.isLocked()) {

      $(`.panorama-viewer-progress`).css('display', 'none');
    }

    return kpc._locked;
  }

  /* check if we are locked */
  isLocked(): boolean {

    let kpc: KRPanoramaViewer = this;

    if ((kpc._locked.target === null) && this._locked.screen) {

      /* neither step is pending */

      return false;
    }

    /* at least one of the two steps is outstanding */

    return true;
  }

  /** fetch the current horizontal angle */
  h(): number {

    return this._h;
  }

  /** fetch the current vertical angle */
  v(): number {

    return this._v;
  }

  /** fetch the current FOV */
  fov(): number {

    return this._fov;
  }

  /** tear down the KRPano viewer */
  ngOnDestroy() {

    window.removepano(KRPanoramaViewer.domId);
  }

  /** fetch the metadata for currently loaded panorama */
  meta(): Panorama {

    return this._meta;
  }

  onError(msg) {

    Logger.error('[KRPanoramaViewer] ', msg);

    this.alerts.error(this.krpano.lasterror);
  }

  /* publish the KRPano events to any observers */
  publishEvent(e: Event) {

    if (this.loggingEvents) {

      Logger.debug('[KRPanoramaViewer] publishEvent - ', e);
    }

    /* emit the event to parent components that are watching */

    this.pano_event.emit(e);
  }

  /** focus on this panorama */
  focus(pid) {

    let kpc: KRPanoramaViewer = this;

    /* do we have this one cached already? */

    let pano: Panorama = kpc.panos.getPanorama(pid);

    if (pano) {

      /* just use the cached panorama */

      Logger.debug(`[KRPanoramaViewer] focus - jumping to cached pano ${pano.id}...`);

      kpc.jumpTo(pano);

      return ;
    }

    /* we have to go get it... */

    kpc.panos.panorama(pid)
      .pipe(first())
      .subscribe(
        (data: any) => {

          if (data.result.next) {

            data.result.panorama.next = data.result.next;
          }

          if (data.result.previous) {

            data.result.panorama.previous = data.result.previous;
          }

          if (data.result.left) {

            data.result.panorama.left = data.result.left;
          }

          if (data.result.right) {

            data.result.panorama.right = data.result.right;

          }

          let pano: Panorama = Panorama.from(data.result.panorama);

          kpc.panos.cachePanorama(pano);

          Logger.debug(`[KRPanoramaViewer] focus - jumping to pano ${pano.id}...`);

          kpc.jumpTo(pano);
        },
        (error) => {

          /* something went horribly, horribly wrong! */

          Logger.error(`[KRPanoramaViewer] can't focus: `, errorAsText(error));

          kpc.alerts.error(errorAsText(error));
        });

  }

  onReady() {

    let kpc: KRPanoramaViewer = this;

    kpc.route.queryParams.subscribe((args: Params) => {

      let params: ParamMap = this.route.snapshot['queryParamMap'];

      /*
       * normally, the "ID" is a numerical OID (row ID) for the panorama in our
       * local PostgreSQL database.  But, if we get something that isn't just
       * an integer, its likely a UUID; i.e. the panorama UUID from BigQuery (pano_db),
       * we can key on that as well, but we have to convert it to the local
       * PostgreSQL ID so that the rest of our code can work as normal.
       *
       * The net effect, is that users can manually use the panorama UUIDs in the
       * view URLs if they really want to; sometimes this is useful for testing
       * or development.
       *
       */

      if (!/^[0-9]+$/.test(params.get('id'))) {

        let uuid: string = params.get('id');

        Logger.debug(`[KRPanoramaViewer] converting UUID: ${uuid} ...`);

        kpc.panos.panoramaOf(uuid)
          .pipe(first())
          .subscribe(
            (data: any) => {

              let pano: Panorama = Panorama.from(data.result.panorama);
              let pid: number    = pano.id;

              Logger.debug(`[KRPanoramaViewer] onReady focusing on PID ${uuid}: ${pid}`);

              kpc.focus(pid);
            },
            (error) => {

              /* something went horribly, horribly wrong! */

              Logger.error(`[KRPanoramaViewer] can't convert UUID to known PID: `, errorAsText(error));

              kpc.alerts.error(errorAsText(error));
            });

        return ;
      }

      let pid: number      = Number(params.get('id'));

      Logger.debug(`[KRPanoramaViewer] onReady focusing on ID: ${pid}`);

      kpc.focus(pid);

    });

  }

  /** jump to a new panorama */
  jumpTo(p: Panorama) {

    let kpc: KRPanoramaViewer = this;

    /* are we already in the process of loading something? */

    if (kpc.isLocked()) {

      Logger.warning(`[KRPanoramaViewer] jumpTo - locked: target=${kpc._locked.target}, screen=${kpc._locked.screen}, trying to jump to ${p.id} ... skipping jump.`);

      return;
    }

    /* are we already viewing it? */

    if (kpc._meta) {

      if (p.id == kpc._meta.id) {

        Logger.warning(`[KRPanoramaViewer] jumpTo - panorama ${p.id} is already being viewed, skipping jump.`);

        return;
      }
    }

    /* lock, so we can overlap jump attempts ... */

    Logger.debug(`[KRPanoramaViewer] jumpTo - locking for pano: ${p.id}`);

    kpc.lock(p);

    /* do it! */

    kpc._previousMeta = kpc._meta;
    kpc._meta         = p;

    /*
     * when we load a specific panorama, it is implicitly the new selection in the
     * panorama selector.  So, tell the panorama service what the new selection is,
     * and it will make sure that gets published to listeners, like the panorama
     * selector.
     *
     */

    kpc.panos.select(new PanoramaSelection({sub: kpc.meta().sub, timeline: kpc.meta().timeline, mile: kpc.meta().mile}));

    /* selection is already set, so whoever is listening can get the panorama and the current selection. */

    kpc.panos.setPanorama(kpc.meta());

    /*
     * But what about the URL in the browser?  The first time we go from overview page to the
     * panorama page, URL will be set and have the right panorama ID.  But, if we are just already on
     * the panorama page, and we are jumping from panorama to panorama...there is no page reload,
     * so the URL isn't updating to track the right 'id' query parameter (i.e. for the panorama we
     * are now on.
     *
     * To ensure the URL 'id' query parameter is up to date, we navigate to ourself, replacing
     * the query parameter we need to...this won't causue a reload, it just updates the URL in
     * the browser.
     *
     * ref: https://stackoverflow.com/questions/43698032/angular-how-to-update-queryparams-without-changing-route
     *
     */

    Logger.debug(`[KRPanoramaViewer] new panorama: `, kpc.meta());

    this.router.navigate([], {
      relativeTo:  this.route,
      queryParams: {
        id: kpc.meta().id
      },
      queryParamsHandling: 'merge'
    });

    Logger.debug(`[KRPanoramaViewer] URL updated. KR Pano loading...`);

    kpc.load(kpc.meta().uuid, !kpc._firstLoad);
  }

  /** fetch the absolute bearing for the current panorama, i.e. truck direction */
  absoluteBearing(pano: Panorama = null): number {

    /* figure out the end points of our line of travel vector */

    let kpc: KRPanoramaViewer = this;
    let a: Panorama           = null;
    let b: Panorama           = null;

    if (!pano) {

      pano = kpc.meta();
    }

    if (pano.previous && pano.next) {

      /*
       * if we have both then line should be from previous to next, and we
       * assume that they are time ordered, with next in the future.
       *
       */

      a = pano.previous;
      b = pano.next;

    } else {

      /* similar but we draw the line from us to next, or from previous to us */

      if (pano.previous) {

        a = pano.previous;
        b = pano;

      } else if (pano.next) {

        a = pano;
        b = pano.next;

      }
    }

    /* https://stackoverflow.com/questions/3932502/calculate-angle-between-two-latitude-longitude-points */

    let dLng: number    = b.y - a.y;

    let y: number       = Math.sin(dLng) * Math.cos(b.x);
    let x: number       = Math.cos(a.x)  * Math.sin(b.x) - Math.sin(a.x) * Math.cos(b.x) * Math.cos(dLng);

    let bearing: number = Math.atan2(y, x) * 180 / Math.PI + 180;

    Logger.debug(`[KRPanoramaViewer]  bearing from: ${a.id} to: ${b.id} bearing: ${bearing}`);

    /* pass it back */

    return bearing;
  }

  /** save the current view direction/zoom, so we can restore after loading a new panorama */
  saveViewParameters() {

    let kpc: KRPanoramaViewer = this;

    kpc._viewRestored         = false;

    kpc._pendingH             = kpc.h();
    kpc._pendingV             = kpc.v();
    kpc._pendingFOV           = kpc.fov();

    /* if we have local storage, cache it there */

    if (typeof(Storage) !== "undefined") {

      localStorage.setItem('krpano.h',   kpc._pendingH.toString());
      localStorage.setItem('krpano.v',   kpc._pendingV.toString());
      localStorage.setItem('krpano.fov', kpc._pendingFOV.toString());
    }

    Logger.debug(`[KRPanoramaViewer] saveViewParameters - h=${kpc._pendingH}, v=${kpc._pendingV}, fov=${kpc._pendingFOV}`);

    /*
     * Establish the preferred absolute bearing; if we load a new panorama, the truck may or may not be
     * facing the same direction so "0" may not be "0" anymore, we have to be ready to dynamically offset
     * or re-zero on each panorama, but still continue on the same general bearing.  We have this problem
     * because the panoramas that are "on the same track" may or may not be from the same capture run, or
     * may be from the same truck but in the other direction; say on the way out vs the way back or vice
     * versa.
     *
     * So as you move along the track, the view cone can flip to the other side, even when you've
     * adjusted to have a view angle offset...because "0" isn't the same "0" anymore.  It wouldn't
     * make sense to try and keep consistency in general, since if you jumped to a completely new
     * track it might be going in a completely different direction regardless of your current bearing.
     *
     * For example, if you are on one panorama, and jump (say by clicking in the minimap) to another
     * separate track and panorama that is perpendicular, do we keep the view offset?  That offset
     * only made sense with old absolute bearing, because it was offset to the direction of travel
     * of the old track.
     *
     * Unfortunately there is no easy fix here, because if you try to automatically sense the
     * direction of the truck, either by the time order of the prev/current/next points, or the
     * mileage...it may or may not be consistent on each load; because if you jump to another one,
     * that panorama you jump to, may or may not be from the same capture run or same direction.
     *
     * The best we can do is try to retain some consistent, when we load, check if we have a
     * preferred absolute bearing, and if we do and the new absolute bearing is 180 degrees away
     * within some tolerance, then we assume the truck direction flipped...and we adjust the krpano
     * view angle to compensate for that.  We don't do it always, because if they are jumping
     * to s different track around a sharp corner, it won't make any more sense to try and be
     * consistent, than to be consistent.
     *
     */

    let bearing: Number = kpc.absoluteBearing(kpc._previousMeta);

    Logger.debug(`[KRPanoramaViewer] saveViewParameters - preferred bearing: ${bearing}`);

    localStorage.setItem('preferred.bearing',   bearing.toString());


  }

  loadViewParameters() {

    let kpc: KRPanoramaViewer = this;

    if (kpc._viewRestored) {

      /* we already called loadViewParameters() to match the most recent saveViewParameters() */

      Logger.warning(`[KRPanoramaViewer] skipping, already called once for most recent saveViewParameters()`);

      return ;
    }

    /* if we have local storage, try to reload what we cached there */

    if (typeof(Storage) !== "undefined") {

      if (localStorage.getItem('krpano.h')) {

        kpc._pendingH = Number(localStorage.getItem('krpano.h'));
      }

      if (localStorage.getItem('krpano.v')) {

        kpc._pendingV = Number(localStorage.getItem('krpano.v'));
      }

      if (localStorage.getItem('krpano.fov')) {

        kpc._pendingFOV = Number(localStorage.getItem('krpano.fov'));
      }
    }

    Logger.debug(`[KRPanoramaViewer] loadViewParameters - h=${kpc._pendingH}, v=${kpc._pendingV}, fov=${kpc._pendingFOV}`);

    /*
     * before we adjust the viewing angle, double-check the bearing, in some cases they may
     * move along the track but the panorama they move to, may be from a different capture run
     * and facing the other direction (or maybe even on a different track altogether. We can't
     * predict anything, because the tracks just have a mess of unrelated panoramas.
     *
     * But, we can try to auto-detect 180 degree flips, when we see tha that happen we offset
     * the view angle by 180, so that even though the truck is in the exact opposite direction,
     * we still retain the viewing offset we had before.  We look for 180 = 180 only within
     * a certain tolerance, so we aren't interfering when they do things like move to a
     * completely new track, or go around a very sharp corner.
     *
     */

    if (localStorage.getItem('preferred.bearing')) {

      let oldB:  number = Number(localStorage.getItem('preferred.bearing'));
      let newB:  number = kpc.absoluteBearing();
      let diff : number = Math.abs(oldB - newB);
      let delta: number = Math.abs(180.0 - diff);

      Logger.debug(`[KRPanoramaViewer] loadViewParameters - bearing diff: ${diff}`);

      if (delta < 5.0) {

        /*
         * anything with a 180-185 degree sudden jump, will be presumed to be truck
         * changing direction.  Offset by 180, so we retrain the viewing angle we had
         * before.
         *
         */

        Logger.debug(`[KRPanoramaViewer] loadViewParameters - truck direction change detected ${oldB} => ${newB}, 180 delta:  ${delta}`);

        Logger.debug(`[KRPanoramaViewer] loadViewParameters - new view angle: ${kpc._pendingH} => ${kpc._pendingH + 180}`);

        if (kpc._pendingH >= 0) {

          kpc._pendingH -= 180;

        } else {

          kpc._pendingH += 180;
        }

        localStorage.setItem('krpano.h',   kpc._pendingH.toString());
      }
    }

    Logger.debug(`[KRPanoramaViewer] loadViewParameters - pending H: ${kpc._pendingH}`);

    /* adjust the view angle to track whatever the users preferred offset has been set to */

    kpc.krpano.actions.lookat(kpc._pendingH, kpc._pendingV, kpc._pendingFOV);

    kpc._viewRestored = true;
  }

  /**
   * load a panoramic view by its UUID.
   *
   * @param panoId the uuid of the panoramic image to load
   * @param blend  set to false if this is the very first load (nothing to blend with)
   *
   */
  load(panoId: string, blend: boolean = true) {

    let kpc: KRPanoramaViewer = this;
    let token: string         = kpc.auth.userOfSession.token;

    /* tell KR Pano to display this new panorama... */

    kpc._firstLoad           = false;

    let blendMode: string    = 'BLEND(0.8,easeInCubic)';
    let flags: string        = 'MERGE';
    let signature: string    = kpc.meta().signature.replace(/&/g, '&amp;');
    let xml: string          = `
<krpano>
  <contextmenu fullscreen="false" versioninfo="false" />
  <control
    mouse="drag"
    touch="drag"
    zoomtocursor="false"
    zoomoutcursor="false"
    draginertia="0.1"
    dragfriction="0.9"
    movetoaccelerate="1.0"
    movetospeed="10.0"
    movetofriction="0.8"
    keybaccelerate="0.09"
    keybfriction="0.94"
    keybfovchange="0.25"
    mousefovchange="1.0"
    fovspeed="3.0"
    fovfriction="0.9"
    bouncinglimits="calc:image.cube ? true : false"
  />
  <display safearea="h-only"/>
  <cursors standard="default" dragging="move" moving="move" />
  <view
    hlookat="${kpc._pendingH}"
    vlookat="${kpc._pendingV}"
    fovtype="MFOV"
    fov="${kpc._pendingFOV}"
    maxpixelzoom="2.0"
    fovmin="70"
    fovmax="140"
    limitview="range"
    vlookatmin="-90.000"
    vlookatmax="61.813"
  />
  <preview
    url="%$ironroad.storage%/${panoId}.tiles/preview.jpg?${signature}"
  />
  <image
    progressive="true"
    >
    <cube
      url="%$ironroad.storage%/${panoId}.tiles/%s/l%l/%v/l%l_%s_%v_%h.jpg?${signature}"
      multires="512,640,1280,2624"
    />
  </image>

</krpano>
`;

    if (!blend) {

      blendMode = 'NOBLEND';
    }

    Logger.debug(`[KRPanoramaViewer] load - pano (${panoId}): `, xml);

    /*
     * save our current looking direction/zoom, and don't let them click again
     * while we are trying to load the next one...creates timing issues and
     * the view direction can get messed up.
     *
     */

    kpc.saveViewParameters();

    kpc.krpano.actions.loadxml(xml, null, flags, blendMode);

  }

  /** zoom in more by some percentage, will stop at fovmax */
  zoomIn(percent: number = 5.0) {

    let current: number = this.zoomPercent();

    this.zoomTo(current + percent);
  }

  /** zoom out more by some percentage, will stop at fovmin */
  zoomOut(percent: number = 5.0) {

    this.zoomTo(this.zoomPercent() - percent);
  }

  /** fetch teh current zoom level as a percentage */
  zoomPercent(): number {

    let kpc: KRPanoramaViewer = this;

    let min: number = kpc.krpano.view.fovmin;
    let max: number = kpc.krpano.view.fovmax;
    let fov: number = kpc.krpano.view.fov;

    return ((fov - min) / (max - min)) * 100.0;
  }

  /** zoom to exactly this percentage; 0 is fovmin and 100% is fovmax */
  zoomTo(percent: number = 50.0) {

    let kpc: KRPanoramaViewer = this;

    let min: number = kpc.krpano.view.fovmin;
    let max: number = kpc.krpano.view.fovmax;

    if (percent < 0.0) {

      percent = 0.0;
    }

    if (percent > 100.0) {

      percent = 100;
    }

    kpc.krpano.actions.zoomto((max - min) * (percent / 100.0) + min, 'tween(easeinquad, 0.3)');
  }

  /** normalize an angle to be from -179 to 180 degrees */
  normalize180(angle: number) {

    /* https://stackoverflow.com/questions/1628386/normalise-orientation-between-0-and-360 */

    angle = ( (angle % 360) + 360) % 360;

    if (angle > 180) {

      angle -= 360;
    }

    return angle;
  }

  /** rotate horizontally to the left by delta degrees */
  lookLeft(delta: number = 10) {

    let kpc: KRPanoramaViewer = this;

    let h = kpc.krpano.view.hlookat;

    h -= delta;

    kpc.lookTo(h, kpc.krpano.view.vlookat, kpc.krpano.view.fov);
  }

  /** rotate horizontally to the right by delta degrees */
  lookRight(delta: number = 10) {

    let kpc: KRPanoramaViewer = this;

    let h = kpc.krpano.view.hlookat;

    h += delta;

    kpc.lookTo(h, kpc.krpano.view.vlookat, kpc.krpano.view.fov);
  }

  /** rotate vertically to the left by delta degrees */
  lookUp(delta: number = 10) {

    let kpc: KRPanoramaViewer = this;

    let v = kpc.krpano.view.vlookat;

    v -= delta;

    kpc.lookTo(kpc.krpano.view.hlookat, v, kpc.krpano.view.fov);
  }

  /** rotate vertically to the right by delta degrees */
  lookDown(delta: number = 10) {

    let kpc: KRPanoramaViewer = this;

    let v = kpc.krpano.view.vlookat;

    v += delta;

    kpc.lookTo(kpc.krpano.view.hlookat, v, kpc.krpano.view.fov);
  }

  /** animate the view to the target (h)horizontal angle, (v)ertical angle and FOV */
  lookTo(h: number, v: number, fov: number = 0) {

    let kpc: KRPanoramaViewer = this;

    if (fov == 0) {

      fov = kpc.krpano.view.fov;
    }

    h = kpc.normalize180(h);
    v = kpc.normalize180(v);

    kpc.krpano.actions.lookto(h, v, fov, 'tween(easeinquad, 0.3)');
  }

  /** return to default view position */
  resetLook() {

    this.lookTo(0,0, 90);
  }
}
